/**
 * useAjustesAguardando — contador de ajustes de estoque aguardando aprovação
 * para o badge da sidebar em Suprimentos > Movimentos (Bloco 4, 12/09/2026).
 *
 * Espelho de `useDisposicaoPendentes`: só roda autenticado e com
 * `estoque:aprovar` (quem não pode aprovar não recebe promessa de trabalho),
 * polling lento, cache module-level para dedupe entre montagens, e o evento
 * `ajustes-aguardando-mudou` força re-leitura logo após aprovar/recusar ou
 * após um lançamento cair na fila (202).
 */
import { useCallback, useEffect, useState } from "react";

import { useAuth } from "../context/AuthContext";

const POLL_MS = 120_000;
const DEDUPE_MS = 30_000;

export const EVENTO_AJUSTES_MUDOU = "ajustes-aguardando-mudou";

let cache = { total: 0, em: 0 };

/** Notifica os badges montados que a fila mudou (chamar após decidir). */
export function notificarAjustesMudou() {
  window.dispatchEvent(new CustomEvent(EVENTO_AJUSTES_MUDOU));
}

export default function useAjustesAguardando({ enabled = true } = {}) {
  const { request, hasPermission, authenticated } = useAuth();
  const ativo = Boolean(enabled) && Boolean(authenticated) && hasPermission("estoque:aprovar");
  const [total, setTotal] = useState(cache.total);

  const carregar = useCallback(
    async (forcar = false) => {
      if (!ativo) return;
      if (!forcar && Date.now() - cache.em < DEDUPE_MS) {
        setTotal(cache.total);
        return;
      }
      try {
        const p = await request("/api/ajustes-pendentes?estado=AGUARDANDO&page_size=1", { silentError: true });
        const qtd = typeof p?.count === "number" ? p.count : Array.isArray(p) ? p.length : (p?.results || []).length;
        cache = { total: qtd, em: Date.now() };
        setTotal(qtd);
      } catch {
        // Rede ou backend atrás: mantém o último total conhecido.
      }
    },
    [ativo, request],
  );

  useEffect(() => {
    if (!ativo) return undefined;
    carregar();
    const timer = setInterval(() => carregar(true), POLL_MS);
    const aoMudar = () => carregar(true);
    window.addEventListener(EVENTO_AJUSTES_MUDOU, aoMudar);
    return () => {
      clearInterval(timer);
      window.removeEventListener(EVENTO_AJUSTES_MUDOU, aoMudar);
    };
  }, [ativo, carregar]);

  return total;
}
